/**
 * Global word dictionary management UI (body only — no page/dialog chrome).
 * Used both by the standalone DictionaryPage and the combined ManagementModal.
 *
 * Accessibility notes (ADA / WCAG 2.1 AA):
 * - Always Redact / Allow List is a real Radix tablist, so it gets role=tab,
 *   aria-selected, arrow-key navigation and a linked tabpanel for free.
 * - Every control has a programmatic name: sr-only <label>s for the text
 *   inputs, aria-label on the selects, aria-label on the icon-only buttons
 *   (the tooltip alone is only an aria-describedby, and only while it's open).
 * - Focus is visible everywhere (ring, not just a border-colour change) and is
 *   moved into the row editor on Edit and back to the Edit button on exit.
 * - The table wraps long words/reasons instead of pushing the layout wide,
 *   which is what keeps it usable at 400% zoom (WCAG 1.4.10 Reflow).
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Plus, Pencil, Trash2, Check, Loader2, X, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/Button';
import { IconTooltip } from '../ui/tooltip';
import { apiHelpers, dictionaryApi } from '../../lib/api';
import {
  ALL_CATEGORIES,
  NO_CATEGORY,
  categoriesOf,
  filterByCategory,
  hasUncategorized,
  saveBlob,
} from '../../lib/dictionaryImport';
import { AllowListImportDialog } from './AllowListImportDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CharCount } from '../ui/CharCount';
import { DeleteDictionaryEntryDialog } from '../DeleteDictionaryEntryDialog';
import { useDictionary } from '../../hooks/useDictionary';
import type { WordDictionaryEntry } from '../../types';

// Mirrors the server's validation limits (server/routers/dictionaries.py) so the
// form can't submit a payload the API will reject with a 422.
const WORD_MAX_LENGTH = 500;
const REASON_MAX_LENGTH = 500;

type ActiveTab = 'always_allow' | 'always_redact';

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'always_redact', label: 'Always Redact' },
  { value: 'always_allow', label: 'Allow List' },
];

// Shared focus treatment: a ring, so focus is never signalled by colour alone.
const FOCUS_RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1';
const INPUT_CLASS =
  'border border-border rounded-md text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/70';

export function DictionaryManager({ initialTab = 'always_redact' }: { initialTab?: ActiveTab } = {}) {
  const { entries, isLoading, error, addEntry, updateEntry, deleteEntry, refresh } = useDictionary('global');

  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

  // Add form state
  const [newWord, setNewWord] = useState('');
  const [newMatchType, setNewMatchType] = useState<'exact' | 'contains'>('exact');
  const [newReason, setNewReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingWord, setEditingWord] = useState('');
  const [editingMatchType, setEditingMatchType] = useState<'exact' | 'contains'>('exact');
  const [editingReason, setEditingReason] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<WordDictionaryEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Occurrence counts (blast radius) per entry id, across all of the tenant's jobs.
  // null while loading / on error — the column then renders a neutral placeholder.
  const [occurrenceCounts, setOccurrenceCounts] = useState<Record<string, number> | null>(null);

  // Allow List import / export (sc-11468) and the category filter.
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORIES);
  const [importOpen, setImportOpen] = useState(false);
  const [downloading, setDownloading] = useState<'export' | 'template' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Field ids — the labels are visually hidden, so they still need to point at
  // a real input (a placeholder is not an accessible name).
  const fieldId = useId();
  const wordFieldId = `${fieldId}-word`;
  const reasonFieldId = `${fieldId}-reason`;
  const editWordFieldId = `${fieldId}-edit-word`;
  const editReasonFieldId = `${fieldId}-edit-reason`;

  // Focus management for the inline row editor: into the word field on open,
  // back to the row's Edit button on save/cancel (the button is a fresh DOM
  // node by then, so it's looked up by id rather than held as a ref).
  const editingWordRef = useRef<HTMLInputElement | null>(null);
  const editButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  useEffect(() => {
    if (editingId) editingWordRef.current?.focus();
  }, [editingId]);

  useEffect(() => {
    if (!pendingFocusId) return;
    editButtonRefs.current[pendingFocusId]?.focus();
    setPendingFocusId(null);
  }, [pendingFocusId]);

  const fetchCounts = useCallback(async () => {
    try {
      const { counts } = await dictionaryApi.getOccurrenceCounts({ scope_type: 'global' });
      setOccurrenceCounts(counts);
    } catch (err) {
      // Non-critical enrichment — keep the dictionary usable if counts fail.
      setOccurrenceCounts(null);
      console.error('Failed to fetch dictionary occurrence counts:', err);
    }
  }, []);

  // Refetch when the entry set changes (add/edit/delete triggers a backend rescan
  // that can move the counts), so the displayed blast radius stays roughly current.
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, entries]);

  const activeLabel = activeTab === 'always_redact' ? 'Always Redact' : 'Allow List';
  const showReasonColumn = activeTab === 'always_redact';
  // Categories come from the list import; the Allow List shows them as a column + filter.
  const showCategoryColumn = activeTab === 'always_allow';
  const tabEntries = entries.filter((e) => e.action === activeTab);
  const categories = showCategoryColumn ? categoriesOf(tabEntries) : [];
  const showCategoryFilter = categories.length > 0;
  // A filter value that no longer exists (e.g. after a Replace) falls back to all.
  const effectiveFilter =
    categoryFilter === ALL_CATEGORIES ||
    (categoryFilter === NO_CATEGORY && hasUncategorized(tabEntries)) ||
    categories.includes(categoryFilter)
      ? categoryFilter
      : ALL_CATEGORIES;
  const filteredEntries = showCategoryColumn ? filterByCategory(tabEntries, effectiveFilter) : tabEntries;

  const handleDownload = async (kind: 'export' | 'template') => {
    setDownloading(kind);
    setDownloadError(null);
    try {
      if (kind === 'export') {
        saveBlob(await dictionaryApi.exportList('always_allow'), 'allow-list.csv');
      } else {
        saveBlob(await dictionaryApi.getImportTemplate('always_allow'), 'allow-list-template.csv');
      }
    } catch (err) {
      setDownloadError(apiHelpers.handleError(err));
    } finally {
      setDownloading(null);
    }
  };

  const handleAdd = async () => {
    if (!newWord.trim()) return;
    setIsSubmitting(true);
    const result = await addEntry({
      word: newWord.trim(),
      action: activeTab,
      matchType: newMatchType,
      reason: activeTab === 'always_redact' ? newReason.trim() || undefined : undefined,
    });
    setIsSubmitting(false);
    if (result) {
      setNewWord('');
      setNewReason('');
      setNewMatchType('exact');
    }
  };

  const handleStartEdit = (entry: WordDictionaryEntry) => {
    setEditingId(entry.id);
    setEditingWord(entry.word);
    setEditingMatchType(entry.match_type);
    setEditingReason(entry.reason || '');
  };

  const handleCancelEdit = () => {
    setPendingFocusId(editingId);
    setEditingId(null);
    setEditingWord('');
    setEditingReason('');
    setEditingMatchType('exact');
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingWord.trim()) return;
    setIsSubmitting(true);
    const result = await updateEntry(editingId, {
      word: editingWord.trim(),
      matchType: editingMatchType,
      reason: activeTab === 'always_redact' ? editingReason.trim() || undefined : undefined,
    });
    setIsSubmitting(false);
    if (result) {
      handleCancelEdit();
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setIsDeleting(true);
    await deleteEntry(target.id);
    setIsDeleting(false);
  };

  const renderOccurrenceCount = (entry: WordDictionaryEntry) => {
    if (occurrenceCounts === null) {
      return (
        <>
          <span aria-hidden="true">—</span>
          <span className="sr-only">Count unavailable</span>
        </>
      );
    }
    return <span className="tabular-nums">{(occurrenceCounts[entry.id] ?? 0).toLocaleString()}</span>;
  };

  const renderCategory = (entry: WordDictionaryEntry) =>
    entry.category || (
      <>
        <span aria-hidden="true">—</span>
        <span className="sr-only">No category</span>
      </>
    );

  const renderPanel = () => (
    <>
      {showCategoryColumn && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="allow-list-toolbar">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" aria-hidden="true" />
            Import…
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload('export')}
            disabled={downloading !== null}
            aria-busy={downloading === 'export'}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDownload('template')}
            disabled={downloading !== null}
            aria-busy={downloading === 'template'}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            Download template
          </Button>
          {downloadError && (
            <span role="alert" className="text-sm text-red-600">
              {downloadError}
            </span>
          )}
        </div>
      )}

      {/* Add entry form */}
      <form
        className="bg-white border border-border rounded-lg p-4 mb-6"
        onSubmit={(e) => {
          e.preventDefault();
          handleAdd();
        }}
      >
        <h3 className="text-sm font-medium mb-3">Add to {activeLabel}</h3>
        <div className="flex gap-2 items-start flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <label htmlFor={wordFieldId} className="sr-only">
              Word or phrase to add to {activeLabel}
            </label>
            <input
              id={wordFieldId}
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Word or phrase"
              maxLength={WORD_MAX_LENGTH}
              className={`h-9 px-3 py-2 w-full ${INPUT_CLASS}`}
            />
            <CharCount value={newWord} max={WORD_MAX_LENGTH} />
          </div>
          <Select
            value={newMatchType}
            onValueChange={(v) => setNewMatchType(v as 'exact' | 'contains')}
          >
            <SelectTrigger
              aria-label="Match type for the new entry"
              className={`w-[150px] bg-white border-border focus-visible:border-primary ${FOCUS_RING}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exact">Exact match</SelectItem>
              <SelectItem value="contains">Contains</SelectItem>
            </SelectContent>
          </Select>
          {showReasonColumn && (
            <div className="flex-1 min-w-[180px]">
              <label htmlFor={reasonFieldId} className="sr-only">
                Redaction reason for the new entry (optional)
              </label>
              <input
                id={reasonFieldId}
                type="text"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="Reason (optional)"
                maxLength={REASON_MAX_LENGTH}
                className={`h-9 px-3 py-2 w-full ${INPUT_CLASS}`}
              />
              <CharCount value={newReason} max={REASON_MAX_LENGTH} />
            </div>
          )}
          <Button
            type="submit"
            disabled={!newWord.trim() || isSubmitting}
            aria-busy={isSubmitting}
            className="shrink-0"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
            )}
            Add
          </Button>
        </div>
      </form>

      {/* A failed add/edit/delete must not take the table down with it, so the
          error is a banner beside the list rather than a branch instead of it. */}
      {error && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 break-words whitespace-pre-wrap"
        >
          {error}
        </div>
      )}

      {showCategoryFilter && (
        <div className="flex items-center gap-2 mb-3">
          <Select value={effectiveFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger
              size="sm"
              aria-label="Filter by category"
              className={`w-[240px] bg-white border-border focus-visible:border-primary ${FOCUS_RING}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
              {hasUncategorized(tabEntries) && <SelectItem value={NO_CATEGORY}>No category</SelectItem>}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground tabular-nums" role="status">
            {filteredEntries.length.toLocaleString()} of {tabEntries.length.toLocaleString()} entries
          </span>
        </div>
      )}

      {/* Entries list */}
      {isLoading ? (
        <div className="flex justify-center py-12" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">Loading dictionary entries…</span>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {showCategoryColumn
            ? 'No entries yet. Add a word or phrase above, or import a list.'
            : 'No entries yet. Add a word or phrase above.'}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <table className="w-full table-fixed text-sm">
            <caption className="sr-only">
              {activeLabel} dictionary entries. The Occurrences column shows how many times each
              word currently appears in detected text across all your documents.
            </caption>
            <thead className="bg-muted/50">
              <tr>
                <th scope="col" className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Word / Phrase
                </th>
                <th
                  scope="col"
                  className="text-left px-4 py-3 font-medium text-muted-foreground w-[140px]"
                >
                  Match
                </th>
                {showReasonColumn && (
                  <th
                    scope="col"
                    className="text-left px-4 py-3 font-medium text-muted-foreground w-[34%]"
                  >
                    Reason
                  </th>
                )}
                {showCategoryColumn && (
                  <th
                    scope="col"
                    className="text-left px-4 py-3 font-medium text-muted-foreground w-[26%]"
                  >
                    Category
                  </th>
                )}
                <th
                  scope="col"
                  className="text-right px-4 py-3 font-medium text-muted-foreground w-32 whitespace-nowrap"
                  title="How many times this word currently appears in detected text across all your documents"
                >
                  Occurrences
                </th>
                <th scope="col" className="px-4 py-3 w-24">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-muted/20">
                  {editingId === entry.id ? (
                    <>
                      <td className="px-4 py-2">
                        <label htmlFor={editWordFieldId} className="sr-only">
                          Word or phrase
                        </label>
                        <input
                          id={editWordFieldId}
                          ref={editingWordRef}
                          type="text"
                          value={editingWord}
                          onChange={(e) => setEditingWord(e.target.value)}
                          maxLength={WORD_MAX_LENGTH}
                          className={`h-8 px-2 py-1 w-full ${INPUT_CLASS}`}
                        />
                        <CharCount value={editingWord} max={WORD_MAX_LENGTH} />
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={editingMatchType}
                          onValueChange={(v) => setEditingMatchType(v as 'exact' | 'contains')}
                        >
                          <SelectTrigger
                            size="sm"
                            aria-label={`Match type for ${entry.word}`}
                            className={`w-full px-2 bg-white border-border focus-visible:border-primary ${FOCUS_RING}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">Exact</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      {showReasonColumn && (
                        <td className="px-4 py-2">
                          <label htmlFor={editReasonFieldId} className="sr-only">
                            Redaction reason
                          </label>
                          <input
                            id={editReasonFieldId}
                            type="text"
                            value={editingReason}
                            onChange={(e) => setEditingReason(e.target.value)}
                            placeholder="Reason"
                            maxLength={REASON_MAX_LENGTH}
                            className={`h-8 px-2 py-1 w-full ${INPUT_CLASS}`}
                          />
                          <CharCount value={editingReason} max={REASON_MAX_LENGTH} />
                        </td>
                      )}
                      {showCategoryColumn && (
                        <td className="px-4 py-2 text-muted-foreground break-words">{renderCategory(entry)}</td>
                      )}
                      <td className="px-4 py-2 text-right text-muted-foreground">
                        {renderOccurrenceCount(entry)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <IconTooltip label="Save changes">
                            <button
                              type="button"
                              onClick={handleSaveEdit}
                              disabled={isSubmitting}
                              aria-label={`Save changes to ${entry.word}`}
                              className={`p-1 hover:bg-green-100 text-green-800 rounded disabled:opacity-50 ${FOCUS_RING}`}
                            >
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </IconTooltip>
                          <IconTooltip label="Cancel editing">
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              aria-label={`Cancel editing ${entry.word}`}
                              className={`p-1 hover:bg-gray-100 text-muted-foreground rounded ${FOCUS_RING}`}
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </IconTooltip>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 font-mono break-words">{entry.word}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {entry.match_type === 'exact' ? 'Exact' : 'Contains'}
                      </td>
                      {showReasonColumn && (
                        <td className="px-4 py-3 text-muted-foreground break-words">
                          {entry.reason || (
                            <>
                              <span aria-hidden="true">—</span>
                              <span className="sr-only">No reason</span>
                            </>
                          )}
                        </td>
                      )}
                      {showCategoryColumn && (
                        <td className="px-4 py-3 text-muted-foreground break-words">{renderCategory(entry)}</td>
                      )}
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {renderOccurrenceCount(entry)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <IconTooltip label="Edit entry">
                            <button
                              type="button"
                              ref={(el) => {
                                editButtonRefs.current[entry.id] = el;
                              }}
                              onClick={() => handleStartEdit(entry)}
                              aria-label={`Edit ${entry.word}`}
                              className={`p-1 hover:bg-gray-100 text-muted-foreground rounded ${FOCUS_RING}`}
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </IconTooltip>
                          <IconTooltip label="Delete entry">
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(entry)}
                              disabled={isDeleting}
                              aria-label={`Delete ${entry.word}`}
                              className={`p-1 hover:bg-red-100 text-red-600 rounded disabled:opacity-50 ${FOCUS_RING}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </IconTooltip>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Words defined here apply across all cases (tenant-wide).
      </p>

      {/* Always Redact / Allow List tabs (Darwin "Tabs Secondary" — segmented, no shadow) */}
      <TabsPrimitive.Root
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ActiveTab)}
      >
        <TabsPrimitive.List
          aria-label="Dictionary list"
          className="flex w-fit items-center overflow-hidden rounded-[4px] border border-border mb-6"
        >
          {TABS.map(({ value, label }) => (
            <TabsPrimitive.Trigger
              key={value}
              value={value}
              className={`inline-flex h-8 items-center justify-center px-[13px] text-xs leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
                activeTab === value
                  ? 'bg-[#31343a] font-bold text-white focus-visible:ring-white'
                  : 'bg-white font-normal text-muted-foreground hover:bg-[#e1e7f1] hover:text-[#31343a] focus-visible:ring-primary'
              }`}
            >
              {label}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>

        {TABS.map(({ value }) => (
          <TabsPrimitive.Content key={value} value={value} className="focus:outline-none">
            {renderPanel()}
          </TabsPrimitive.Content>
        ))}
      </TabsPrimitive.Root>

      <AllowListImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          refresh();
        }}
      />

      <DeleteDictionaryEntryDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTarget(null);
        }}
        word={deleteTarget?.word ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
